import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ExternalLink, Trash2, Loader2 } from 'lucide-react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { ProducerTeamSection } from '@/components/admin/ProducerTeamSection';
import { AdminBillingSection } from '@/components/admin/AdminBillingSection';
import { ProducerActivityLog } from '@/components/admin/ProducerActivityLog';
import { AdminEffectiveFeaturesPanel } from '@/components/admin/AdminEffectiveFeaturesPanel';
import { HostHealthSummaryCard } from '@/components/admin/HostHealthSummaryCard';
import { AdminVerificationSection } from '@/components/admin/AdminVerificationSection';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAdminProducerContext } from '@/context/AdminProducerContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { FileText, MapPin, Phone, Tag, ImageIcon, Eye, Users } from 'lucide-react';

export default function AdminProducerOverview() {
  const { producerId } = useParams<{ producerId: string }>();
  const { getProducerById, deleteProducer, toggleStatus, updateProducer } = useAdminProducerContext();
  const { toast } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [producer, setProducer] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | undefined>();
  // producerFeatures removed — replaced by AdminEffectiveFeaturesPanel

  // Get current user ID
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setCurrentUserId(session?.user?.id);
    });
  }, []);

  // Fetch producer on mount
  useEffect(() => {
    const loadProducer = async () => {
      if (!producerId) return;
      
      setIsLoading(true);
      try {
        const data = await getProducerById(producerId);
        setProducer(data || null);
      } catch (err) {
        console.error('Failed to load producer:', err);
        setProducer(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadProducer();
  }, [producerId, getProducerById]);

  if (isLoading) {
    return (
      <AdminLayout producerId={producerId}>
        <div className="max-w-2xl mx-auto text-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto" />
          <p className="text-muted-foreground mt-4">Laddar producent...</p>
        </div>
      </AdminLayout>
    );
  }

  if (!producer) {
    return (
      <AdminLayout producerId={producerId}>
        <div className="max-w-2xl mx-auto text-center py-16">
          <h1 className="text-xl font-medium text-foreground mb-2">
            Producenten hittades inte
          </h1>
          <p className="text-muted-foreground mb-6">
            Producenten du söker finns inte längre.
          </p>
          <Button asChild>
            <Link to="/admin/producers">Tillbaka till listan</Link>
          </Button>
        </div>
      </AdminLayout>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleDelete = async () => {
    try {
      await deleteProducer(producer.id);
      toast({
        title: 'Värd borttagen',
        description: 'Värden har tagits bort.',
      });
      window.location.href = '/admin/producers';
    } catch (err) {
      toast({
        title: 'Kunde inte ta bort',
        description: 'Något gick fel. Försök igen.',
        variant: 'destructive',
      });
    }
  };

  const handleToggleStatus = async () => {
    try {
      await toggleStatus(producer.id);
      const updated = await getProducerById(producer.id);
      if (updated) {
        setProducer(updated);
      }
    } catch (err) {
      toast({
        title: 'Kunde inte ändra status',
        description: 'Något gick fel. Försök igen.',
        variant: 'destructive',
      });
    }
  };

  const quickLinks = [
    { label: 'Grundinfo', href: `/app/admin/producers/${producerId}/grundinfo`, icon: FileText },
    { label: 'Plats', href: `/app/admin/producers/${producerId}/plats`, icon: MapPin },
    { label: 'Kontakt', href: `/app/admin/producers/${producerId}/kontakt`, icon: Phone },
    { label: 'Kategorier', href: `/app/admin/producers/${producerId}/kategorier`, icon: Tag },
    { label: 'Bilder', href: `/app/admin/producers/${producerId}/bilder`, icon: ImageIcon },
    { label: 'Publicering', href: `/app/admin/producers/${producerId}/publicering`, icon: Eye },
  ];

  return (
    <AdminLayout
      producerId={producerId}
      producerName={producer.name}
      title="Översikt"
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">{producer.name}</h1>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant={producer.status === 'public' ? 'default' : 'secondary'}>
                {producer.status === 'public' ? 'Publicerad' : 'Utkast'}
              </Badge>
              {producer.verified && (
                <Badge variant="outline" className="text-green-700 border-green-300">
                  Verifierad
                </Badge>
              )}
            </div>
          </div>

          {producer.status === 'public' && producer.slug && (
            <Button variant="outline" size="sm" asChild>
              <Link to={`/producer/${producer.slug}`} target="_blank">
                <ExternalLink className="h-4 w-4 mr-2" />
                Visa publikt
              </Link>
            </Button>
          )}
        </div>

        {/* Metadata */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <span>Skapad: {formatDate(producer.createdAt)}</span>
          <span>•</span>
          <span>Uppdaterad: {formatDate(producer.updatedAt)}</span>
        </div>

        {/* Quick links grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {quickLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors"
            >
              <link.icon className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm font-medium">{link.label}</span>
            </Link>
          ))}
        </div>

        {/* Host Health summary */}
        {producerId && <HostHealthSummaryCard producerId={producerId} />}

        {/* Verification section */}
        <AdminVerificationSection
          producer={producer}
          onUpdate={updateProducer}
          onRefresh={async () => {
            const updated = await getProducerById(producer.id);
            if (updated) setProducer(updated);
          }}
        />

        {/* Effective Access panel (replaces old features card) */}
        <AdminEffectiveFeaturesPanel producerId={producerId} />

        {/* Team section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4" />
              Teammedlemmar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ProducerTeamSection producerId={producer.id} currentUserId={currentUserId} />
          </CardContent>
        </Card>

        {/* Billing / Test settings section */}
        <AdminBillingSection producerId={producer.id} />

        {/* Danger zone */}
        <Card className="border-destructive/30">
          <CardHeader>
            <CardTitle className="text-base text-destructive">Farozon</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {producer.status === 'public' && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleToggleStatus}
                >
                  Avpublicera
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                className="text-destructive border-destructive/30 hover:bg-destructive/10"
                onClick={() => setDeleteDialogOpen(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Ta bort producent
              </Button>
            </div>
          </CardContent>
        </Card>
        {/* Activity log */}
        <Card>
          <ProducerActivityLog producerId={producer.id} />
        </Card>
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-background">
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort producent?</AlertDialogTitle>
            <AlertDialogDescription>
              Är du säker på att du vill ta bort "{producer.name}"? 
              All relaterad data (produkter, bilder, medlemskap) kommer att raderas permanent. 
              Denna åtgärd kan inte ångras.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Ta bort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
